import React, { useEffect, useState, forwardRef, useImperativeHandle } from "react";

import { Box, Grid, IconButton, MenuItem, Modal, SelectChangeEvent } from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import useStyles from "./styles";
import { AutoSuggest, Button, Checkbox, Input, Select } from "Components/Form";
import { Color } from "Components/Form/Button";
import { useAppState } from "Context/StateContext";
import { Location } from "Models/location";
import { Status } from "Util/enums";
import MapInput from "Components/Form/MapInput";

import { Contact } from "Models/contact";
import { createLocationValidationSchema } from "../../../schemas";

interface Props {
  contacts?: Contact[];
  handleClose?: (location?: Location) => void;
}

export interface LocationModalRef {
  open: (location?: Location) => void;
}

const LocationModal = forwardRef<LocationModalRef, Props>((props, ref) => {
  const { currentUser } = useAppState();

  const { handleClose, contacts } = props;

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [location, setLocation] = useState<Location | undefined>();

  const [locationName, setLocationName] = useState<string>();
  const [status, setStatus] = useState<string>();
  const [contactPerson, setContactPerson] = useState<Contact>();
  const [address, setAddress] = useState<Location>();

  const [includeCommunications, setIncludeCommunications] = useState<boolean>(false);

  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [errors, setErrors] = useState<yup.ValidationError[]>([]);

  const classes = useStyles();

  const { t: translate } = useTranslation();

  useImperativeHandle(ref, () => ({
    open: (locationToEdit?: Location) => {
      setLocation(locationToEdit);
      setIsOpen(true);
    },
  }));

  useEffect(() => {
    if (isSubmitted) handleValidate();
  }, [isSubmitted, locationName, contactPerson, status, address]);

  useEffect(() => {
    setLocationName(location?.name);
    setStatus(location?.status || Status.ACTIVE);
    setAddress(location);
    setContactPerson(location?.contact ? location.contact : undefined);
  }, [location, isOpen]);

  useEffect(() => {
    if (isOpen === false) {
      setLocationName(undefined);
      setStatus(undefined);
      setAddress(undefined);
      setIncludeCommunications(false);
      setContactPerson(undefined);
      setIsSubmitted(false);
      setErrors([]);
      setLocation(undefined);
    }
  }, [isOpen]);

  const handleModalClose = () => {
    setIsOpen(false);
    if (handleClose) {
      handleClose();
    }
  };

  const handleValidate = () => {
    try {
      const schema = createLocationValidationSchema({ translate });

      schema.validateSync(
        {
          locationName,
          contactPerson: contactPerson?.firstName,
          status,
          address: address?.longAddress,
        },
        { abortEarly: false }
      );

      setErrors([]);

      return true;
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        setErrors(error?.inner);
      }

      return false;
    }
  };

  const handleSubmit = () => {
    setIsSubmitted(true);
    if (handleValidate()) {
      const updatedLocation = {
        name: locationName,
        contact: contactPerson,
        contactId: contactPerson?.id,
        status,
        valid: true,
        companyId: currentUser?.companyId,
        ...address,
      } as Location;

      if (handleClose) {
        if (location && location?.id) {
          handleClose({ ...location, ...updatedLocation });
        } else {
          handleClose(updatedLocation);
        }
      }

      setIsOpen(false);
    }
  };

  const fieldHasError = (field: string) => !!errors?.find((error) => error.path === field);

  const getErrorByField = (field: string) => errors?.find((error) => error.path === field)?.message;

  return (
    <Modal
      open={isOpen}
      onClose={handleModalClose}
      aria-labelledby="simple-modal-title"
      aria-describedby="simple-modal-description"
    >
      <div>
        <div className={classes.paper}>
          <div className={classes.modalBody}>
            <Grid container>
              <Grid item xs={12} className={classes.header}>
                <div className={classes.locationModalTitle}>
                  <Box>
                    <span data-cy="locationModalTitle" className={classes.titleName}>
                      {location ? translate("editALocation") : translate("addNewLocation")}
                    </span>
                  </Box>
                  <Box>
                    <IconButton className={classes.iconButton} onClick={handleModalClose}>
                      <CloseIcon data-cy="locationCloseIcon" fontSize="medium" />
                    </IconButton>
                  </Box>
                </div>
              </Grid>
              <Grid item xs={12}>
                <div className={classes.gridContainerLocation}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <Input
                        label={translate("locationName")}
                        onChange={(event) => setLocationName(event.target.value)}
                        required
                        placeholder={translate("enterLocationName")}
                        value={locationName}
                        error={fieldHasError("locationName")}
                        errorMessage={getErrorByField("locationName")}
                        data-cy="location-first-name"
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <AutoSuggest
                        label={translate("contactPerson")}
                        placeholder={translate("selectAContact")}
                        options={contacts}
                        data-cy="consultant"
                        inputValue={
                          contactPerson
                            ? `${contactPerson?.firstName} ${contactPerson?.lastName}`
                            : ""
                        }
                        onChange={(consultant: Contact) => {
                          setContactPerson(consultant);
                        }}
                        getOptionKey={(option) => {
                          return option?.id || 0;
                        }}
                        error={fieldHasError("agentConsultant")}
                        errorMessage={getErrorByField("agentConsultant")}
                        getOptionLabel={(consultant?: Contact) => {
                          const name = consultant?.firstName || "";
                          const lastName = consultant?.lastName || "";

                          return `${name} ${lastName}`;
                        }}
                        required
                        getOptionSelected={(option: Contact, value: Contact) =>
                          option?.id === value?.id
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Select
                        name="agent.status"
                        label={translate("status")}
                        onChange={(event: SelectChangeEvent<any>) => setStatus(event.target.value)}
                        error={fieldHasError("status")}
                        errorMessage={getErrorByField("status")}
                        value={status}
                        required
                        data-cy="status"
                      >
                        <MenuItem value={Status.ACTIVE}>{translate("active")}</MenuItem>
                        <MenuItem value={Status.INACTIVE}>{translate("inactive")}</MenuItem>
                      </Select>
                    </Grid>

                    <Grid item xs={12}>
                      <MapInput
                        location={location}
                        onChange={(value: any) => {
                          setAddress(value);
                        }}
                        error={fieldHasError("address")}
                        errorMessage={getErrorByField("address")}
                        label={translate("address")}
                        inputValue={location?.longAddress}
                        required
                        placeholder="Google Maps search"
                      />
                    </Grid>
                    {false && (
                      <Grid item xs={12}>
                        <Checkbox
                          checked={includeCommunications}
                          onChange={(event, checked) => setIncludeCommunications(checked)}
                          label={translate("includeInCommunications")}
                        />
                      </Grid>
                    )}
                  </Grid>
                </div>
              </Grid>

              <Grid item xs={12} className={classes.locationButtonsContainer}>
                <Button
                  customColor={Color.CANCEL}
                  data-cy="location-cancel-button"
                  onClick={handleModalClose}
                >
                  {translate("cancel")}
                </Button>
                <Button
                  customColor={Color.SUCCESS}
                  data-cy="location-save-button"
                  onClick={() => handleSubmit()}
                  className={classes.buttonSave}
                >
                  {location ? translate("update") : translate("save")}
                </Button>
              </Grid>
            </Grid>
          </div>
        </div>
      </div>
    </Modal>
  );
});

LocationModal.displayName = "LocationModal";

export default LocationModal;
